/**
 * Utility functions for exporting project data as structured JSON.
 * Supports exporting individual cards, columns (lists), project checklists,
 * and entire projects.
 */

/**
 * Export a single card as a structured JSON object.
 * @param {Object} card - The card data object.
 * @param {Object} [options] - Optional context info.
 * @param {string} [options.columnTitle] - Title of the column the card belongs to.
 * @param {string} [options.projectTitle] - Title of the project.
 * @param {Array} [options.projectLabels] - Project labels for resolving label names.
 * @param {Object} [options.projectMembers] - Project members for resolving assignee names.
 * @returns {Object} Structured card export object.
 */
export const buildCardExport = (card, options = {}) => {
    const { columnTitle, projectTitle, projectLabels, projectMembers } = options;

    const resolvedLabels = (card.labelIds || []).map(labelId => {
        const label = (projectLabels || []).find(l => l.id === labelId);
        return label ? { id: label.id, title: label.title, color: label.color } : { id: labelId };
    });

    const resolvedAssignee = card.assigneeId && projectMembers && projectMembers[card.assigneeId]
        ? { id: card.assigneeId, displayName: projectMembers[card.assigneeId].displayName || null, email: projectMembers[card.assigneeId].email || null }
        : card.assigneeId ? { id: card.assigneeId } : null;

    return {
        id: card.id,
        title: card.title,
        description: card.description || null,
        priority: card.priority || null,
        labels: resolvedLabels.length > 0 ? resolvedLabels : undefined,
        assignee: resolvedAssignee || undefined,
        dueDate: card.dueDate || null,
        startDate: card.startDate || null,
        checklists: card.checklists || undefined,
        archived: card.archived || false,
        createdAt: card.createdAt || null,
        updatedAt: card.updatedAt || null,
        link: card.link || undefined,
        ...(columnTitle && { columnTitle }),
        ...(projectTitle && { projectTitle }),
    };
};

/**
 * Export a column (list) with all its cards as a structured JSON object.
 * @param {Object} column - The column data object (with cards array).
 * @param {Object} [options] - Optional context info.
 * @param {string} [options.projectTitle] - Title of the project.
 * @param {Array} [options.projectLabels] - Project labels for resolving label names.
 * @param {Object} [options.projectMembers] - Project members for resolving assignee names.
 * @returns {Object} Structured column export object.
 */
export const buildColumnExport = (column, options = {}) => {
    const { projectTitle, projectLabels, projectMembers } = options;

    return {
        id: column.id,
        title: column.title,
        ...(projectTitle && { projectTitle }),
        cards: (column.cards || []).map(card =>
            buildCardExport(card, { columnTitle: column.title, projectLabels, projectMembers })
        ),
    };
};

/**
 * Export project checklist (todos) as a structured JSON object.
 * @param {Object} project - The project data object.
 * @returns {Object} Structured checklist export object.
 */
export const buildChecklistExport = (project) => {
    return {
        projectId: project.id,
        projectTitle: project.title,
        checklist: (project.todos || []).map(todo => ({
            id: todo.id,
            text: todo.text,
            completed: todo.completed || false,
            color: todo.color || undefined,
        })),
    };
};

/**
 * Export an entire project with all columns, cards, checklist, and metadata.
 * @param {Object} project - The project data object.
 * @param {Object} [options] - Optional context info.
 * @param {Array} [options.columnOrder] - Order of column IDs.
 * @param {Array} [options.projectLabels] - Project labels.
 * @param {Object} [options.projectMembers] - Project members.
 * @returns {Object} Structured project export object.
 */
export const buildProjectExport = (project, options = {}) => {
    const { columnOrder, projectLabels, projectMembers } = options;

    const orderedColumnIds = columnOrder || Object.keys(project.columns || {});

    const columns = orderedColumnIds.map(columnId => {
        const column = (project.columns || {})[columnId];
        if (!column) return null;
        return buildColumnExport(column, { projectLabels, projectMembers });
    }).filter(Boolean);

    return {
        id: project.id,
        title: project.title,
        description: project.description || null,
        tagline: project.tagline || null,
        url: project.url || null,
        background: project.background || undefined,
        labels: projectLabels || undefined,
        checklist: (project.todos || []).map(todo => ({
            id: todo.id,
            text: todo.text,
            completed: todo.completed || false,
            color: todo.color || undefined,
        })),
        columns,
        createdAt: project.createdAt || null,
        updatedAt: project.updatedAt || null,
    };
};

/**
 * Trigger a file download in the browser.
 * @param {Object} data - The data object to export.
 * @param {string} filename - The filename (without extension).
 */
export const downloadJson = (data, filename) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Export a card as JSON file download.
 */
export const exportCardAsJson = (card, options = {}) => {
    const data = buildCardExport(card, options);
    const safeName = (card.title || 'card').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
    downloadJson(data, `card_${safeName}`);
};

/**
 * Export a column (list) as JSON file download.
 */
export const exportColumnAsJson = (column, options = {}) => {
    const data = buildColumnExport(column, options);
    const safeName = (column.title || 'list').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
    downloadJson(data, `list_${safeName}`);
};

/**
 * Export project checklist as JSON file download.
 */
export const exportChecklistAsJson = (project) => {
    const data = buildChecklistExport(project);
    const safeName = (project.title || 'project').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
    downloadJson(data, `checklist_${safeName}`);
};

/**
 * Export entire project as JSON file download.
 */
export const exportProjectAsJson = (project, options = {}) => {
    const data = buildProjectExport(project, options);
    const safeName = (project.title || 'project').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
    downloadJson(data, `project_${safeName}`);
};
